/**
 * React Query hooks for the Agent Arena (RFC-0702). Mirrors api/hooks.ts:
 * a central key map, useQuery for reads, useMutation (invalidating on success).
 */

import { useMutation, useQuery, useQueryClient, type Query } from '@tanstack/react-query'
import * as arena from './arena'

export const arenaKeys = {
  agents: ['arena', 'agents'],
  styles: ['arena', 'styles'],
  runs: ['arena', 'runs'],
  run: (id: string) => ['arena', 'run', id],
  leaderboard: (agentType: string) => ['arena', 'leaderboard', agentType],
}

export function useArenaAgents() {
  return useQuery({ queryKey: arenaKeys.agents, queryFn: () => arena.listArenaAgents() })
}

export function useArenaStyles() {
  return useQuery({ queryKey: arenaKeys.styles, queryFn: () => arena.listArenaStyles() })
}

export function useArenaRuns(limit = 20, offset = 0) {
  return useQuery({
    queryKey: [...arenaKeys.runs, { limit, offset }],
    queryFn: () => arena.listArenaRuns(limit, offset),
  })
}

function _isRunning(detail: arena.ArenaRunDetail | undefined): boolean {
  if (!detail) return true
  if (detail.run.status === 'running') return true
  return detail.results.some((r) => r.status === 'pending' || r.status === 'running')
}

export function useArenaRun(runId: string | undefined) {
  return useQuery({
    queryKey: arenaKeys.run(runId ?? ''),
    queryFn: () => arena.getArenaRun(runId ?? ''),
    enabled: !!runId,
    // Poll while anything is still running, like the generation_jobs poller.
    refetchInterval: (query: Query<arena.ArenaRunDetail>) =>
      _isRunning(query.state.data) ? 2000 : false,
  })
}

export function useCreateArenaRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: arena.CreateArenaRunBody) => arena.createArenaRun(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: arenaKeys.runs })
    },
  })
}

export function useArenaVote(runId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: arena.VoteBody) => arena.voteArena(runId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: arenaKeys.run(runId) })
      // Leaderboard is derived from votes — refresh it too (prefix match).
      qc.invalidateQueries({ queryKey: ['arena', 'leaderboard'] })
    },
  })
}

export function useArenaLeaderboard(agentType: string | undefined) {
  return useQuery({
    queryKey: arenaKeys.leaderboard(agentType ?? ''),
    queryFn: () => arena.getLeaderboard(agentType ?? ''),
    enabled: !!agentType,
  })
}
